/**
 * Tool-Invocation 1.5 — parameterised skill parameter model.
 *
 * A skill's parameters live in its `SKILL.md` frontmatter. PRD Option A captures
 * that manifest into `org_skills.manifest` at install (org-scoped, offline,
 * deterministic — no live GitHub fetch at render). This module owns:
 *
 *   • the {@link SkillParameter} declaration shape the curated form renders,
 *   • {@link parseSkillManifest} — read `manifest.parameters` off a captured row,
 *   • {@link parseSkillFrontmatter} — parse a raw `SKILL.md` at install time,
 *   • {@link fieldKindForParameter} — the declared-type → form-field mapping.
 *
 * MVP scope is the FLAT scalar/enum set (string · multiline · enum · number ·
 * boolean). Anything richer (nested objects, arrays, conditional fields) is a
 * follow-up; an unrecognised entry is dropped, never half-rendered.
 */

/** The declared primitive type of a single skill parameter. */
export type SkillParameterType = 'string' | 'enum' | 'integer' | 'number' | 'boolean';

/** One declared input of a parameterised skill (drives one form field). */
export interface SkillParameter {
  /** The argument key (also the chip `params` key). */
  name: string;
  type: SkillParameterType;
  /** Required params gate Insert chip; optional blanks are omitted from `params`. */
  required: boolean;
  /** Enum choices (only meaningful when `type === 'enum'`). */
  options?: string[];
  /** Render a `string` param as a multi-line textarea rather than a text input. */
  multiline?: boolean;
  /** The skill's own default (shown as a faint placeholder hint; never sent). */
  default?: string | number | boolean;
  /** Optional human label; falls back to `name` when absent. */
  label?: string;
}

/** The form field a parameter renders as (chosen from its declared type). */
export type SkillFieldKind = 'text' | 'textarea' | 'segmented' | 'select' | 'number' | 'toggle';

/** Enum option count at/under which a segmented control is used instead of a select. */
export const SEGMENTED_MAX_OPTIONS = 4;

/**
 * Map a declared parameter to its form field (PRD mapping table):
 *   string → text · string+multiline → textarea · enum ≤4 → segmented ·
 *   enum >4 → select · integer|number → number · boolean → toggle.
 */
export function fieldKindForParameter(p: SkillParameter): SkillFieldKind {
  switch (p.type) {
    case 'boolean':
      return 'toggle';
    case 'integer':
    case 'number':
      return 'number';
    case 'enum':
      return (p.options?.length ?? 0) > SEGMENTED_MAX_OPTIONS ? 'select' : 'segmented';
    case 'string':
    default:
      return p.multiline ? 'textarea' : 'text';
  }
}

function asType(v: unknown): SkillParameterType | null {
  return v === 'string' || v === 'enum' || v === 'integer' || v === 'number' || v === 'boolean'
    ? v
    : null;
}

function asScalarDefault(v: unknown): string | number | boolean | undefined {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? v : undefined;
}

/** Normalize one raw declaration object → a {@link SkillParameter} (or null to drop). */
function normalizeParameter(raw: unknown): SkillParameter | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const name = typeof o.name === 'string' ? o.name.trim() : '';
  const type = asType(o.type);
  if (!name || !type) return null;

  const options =
    Array.isArray(o.options) && o.options.every((x) => typeof x === 'string')
      ? (o.options as string[])
      : undefined;
  // An enum with no usable options can't render a control — drop it.
  if (type === 'enum' && (!options || options.length === 0)) return null;

  const param: SkillParameter = {
    name,
    type,
    required: o.required === true,
    ...(options ? { options } : {}),
    ...(o.multiline === true ? { multiline: true } : {}),
    ...(typeof o.label === 'string' && o.label.trim() ? { label: o.label.trim() } : {})
  };
  const def = asScalarDefault(o.default);
  if (def !== undefined) param.default = def;
  return param;
}

/**
 * Read the declared parameters off a captured manifest (`org_skills.manifest`).
 * Accepts either `{ parameters: [...] }` or a bare `[...]`. Returns `[]` for a
 * NULL / shapeless / parameterless manifest → the skill instant-inserts.
 */
export function parseSkillManifest(manifest: unknown): SkillParameter[] {
  if (!manifest) return [];
  const list = Array.isArray(manifest)
    ? manifest
    : typeof manifest === 'object' && Array.isArray((manifest as Record<string, unknown>).parameters)
      ? ((manifest as Record<string, unknown>).parameters as unknown[])
      : null;
  if (!list) return [];
  const out: SkillParameter[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    const p = normalizeParameter(raw);
    if (p && !seen.has(p.name)) {
      seen.add(p.name);
      out.push(p);
    }
  }
  return out;
}

/** A captured manifest shape (what we store in `org_skills.manifest`). */
export interface SkillManifest {
  parameters: SkillParameter[];
}

/**
 * Parse a raw `SKILL.md` into a captured manifest at install time. Reads the
 * leading `---`-fenced YAML frontmatter and its flat `parameters:` list. Kept
 * dependency-free: the parameter block is a simple flat list of scalar maps, so
 * a focused parser (block-mapping `- name: …` items, inline `- { … }` items, and
 * `[a, b]` option arrays) covers it without pulling in a YAML engine. Returns
 * `{ parameters: [] }` when there's no frontmatter or no parameters.
 */
export function parseSkillFrontmatter(md: string): SkillManifest {
  const fm = extractFrontmatter(md);
  if (!fm) return { parameters: [] };
  const raw = extractParametersBlock(fm);
  return { parameters: parseSkillManifest(raw) };
}

/** Pull the text between the leading `---` fences (CRLF-tolerant). */
function extractFrontmatter(md: string): string | null {
  const m = /^\uFEFF?---\r?\n([\s\S]*?)\r?\n---\s*(?:\r?\n|$)/.exec(md ?? '');
  return m ? m[1] : null;
}

/** Parse a `[a, b, c]` inline flow sequence → trimmed, unquoted strings. */
function parseFlowSeq(s: string): string[] {
  return s
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .split(',')
    .map((x) => unquote(x.trim()))
    .filter((x) => x.length > 0);
}

function unquote(s: string): string {
  if (s.length >= 2 && ((s[0] === '"' && s.endsWith('"')) || (s[0] === "'" && s.endsWith("'")))) {
    return s.slice(1, -1);
  }
  return s;
}

/**
 * Coerce a scalar YAML token → string | number | boolean. A quoted token is
 * always a string (so `"true"` / `"5"` stay strings); a bare `true`/`false` or
 * numeric token is coerced.
 */
function parseScalar(s: string): string | number | boolean {
  const trimmed = s.trim();
  const wasQuoted = trimmed !== unquote(trimmed);
  const t = unquote(trimmed);
  if (wasQuoted) return t;
  if (t === 'true') return true;
  if (t === 'false') return false;
  if (/^-?\d+$/.test(t)) return Number(t);
  if (/^-?\d*\.\d+$/.test(t)) return Number(t);
  return t;
}

/** Parse one `key: value` (or `key: { … }`) into an entry, or null. */
function parseInlineMapEntry(line: string): [string, unknown] | null {
  const idx = line.indexOf(':');
  if (idx < 0) return null;
  const key = line.slice(0, idx).trim();
  const valRaw = line.slice(idx + 1).trim();
  if (!key) return null;
  if (valRaw.startsWith('[')) return [key, parseFlowSeq(valRaw)];
  if (valRaw === '') return [key, ''];
  return [key, parseScalar(valRaw)];
}

/** Parse an inline-flow map `{ name: x, type: y, options: [a, b] }`. */
function parseFlowMap(s: string): Record<string, unknown> {
  const body = s.replace(/^\{/, '').replace(/\}$/, '');
  const out: Record<string, unknown> = {};
  // Split on commas that are NOT inside [...] brackets.
  const parts: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of body) {
    if (ch === '[') depth++;
    if (ch === ']') depth--;
    if (ch === ',' && depth === 0) {
      parts.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) parts.push(cur);
  for (const part of parts) {
    const entry = parseInlineMapEntry(part.trim());
    if (entry) out[entry[0]] = entry[1];
  }
  return out;
}

/**
 * Extract the `parameters:` list from frontmatter text → an array of raw maps.
 * Handles both styles:
 *   parameters:
 *     - { name: q, type: string, required: true }
 *   parameters:
 *     - name: q
 *       type: string
 *       required: true
 */
function extractParametersBlock(fm: string): unknown[] {
  const lines = fm.split(/\r?\n/);
  const startIdx = lines.findIndex((l) => /^parameters\s*:/.test(l.trim()));
  if (startIdx < 0) return [];

  const items: unknown[] = [];
  let current: Record<string, unknown> | null = null;
  const baseIndent = indentOf(lines[startIdx]);

  for (let i = startIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const indent = indentOf(line);
    if (indent <= baseIndent) break; // dedent to a sibling key → block ends

    const trimmed = line.trim();
    if (trimmed.startsWith('- ')) {
      if (current) items.push(current);
      const rest = trimmed.slice(2).trim();
      if (rest.startsWith('{')) {
        items.push(parseFlowMap(rest));
        current = null;
      } else {
        current = {};
        const entry = parseInlineMapEntry(rest);
        if (entry) current[entry[0]] = entry[1];
      }
    } else if (current) {
      const entry = parseInlineMapEntry(trimmed);
      if (entry) current[entry[0]] = entry[1];
    }
  }
  if (current) items.push(current);
  return items;
}

function indentOf(line: string): number {
  const m = /^(\s*)/.exec(line);
  return m ? m[1].length : 0;
}
